import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';

// AI Listing Optimization (R18 #10): pick a listing → pull its live Hostaway
// content → AI proposes a better description, photo order, and captions → approve
// → sync back to Hostaway. Admin/director (edits the public listing).
const API = '/api/assetops';

export class ListingOptimizerPage {
  private container: HTMLElement;
  private listings: any[] = [];
  private selectedId = '';
  private current: any = null;      // GET /listing-optimizer/:id
  private proposal: any = null;     // generated
  private order: string[] = [];     // approved photo order (image ids)
  private captions: Record<string, string> = {};
  private description = '';
  private loading = false;
  private generating = false;
  private syncing = false;
  private note = '';
  // R48 (Jesse): account-wide style/template instructions fed into every
  // generate prompt. Class field so the textarea survives full redraws.
  private optimizerContext = '';
  private ctxOpen = false;
  private ctxSaving = false;
  private ctxLoadFailed = false;
  private ctxVersion = '';   // R114 fix: the stored text's version — the save is refused (409) unless it still matches
  private ctxReloading = false;

  constructor(container: HTMLElement) { this.container = container; }
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="lo-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    const [j, ctx] = await Promise.all([
      fetch('/api/listings/active').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/listing-optimizer-context`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.listings = (j && (j.listings || j)) || [];
    // R48 (Madel): A–Z by the DISPLAYED label — the server orders by raw DB
    // name, which diverges from short_name labels.
    this.listings.sort((a: any, b: any) => String(a.name || a.short_name || '').localeCompare(String(b.name || b.short_name || '')));
    this.optimizerContext = (ctx && ctx.instructions) || '';
    // Review fix: a failed context load must not let a later Save overwrite the
    // stored instructions with the empty fallback (R114 fix2: no textarea at
    // all until a load with a version lands).
    this.ctxLoadFailed = !(ctx && ctx.version);
    this.ctxVersion = (ctx && ctx.version) || '';
    // R115 guest: the Calendar's listing inspector "AI Optimizer" preselects
    // ONE listing (one-shot sessionStorage 'lo_preselect' = pms_listing_id).
    let pre = '';
    try { pre = sessionStorage.getItem('lo_preselect') || ''; if (pre) sessionStorage.removeItem('lo_preselect'); } catch { pre = ''; }
    if (pre && this.listings.some((l: any) => String(l.id || l.pms_listing_id) === pre) && String(this.selectedId) !== pre) {
      this.selectedId = pre;
      this.draw();
      await this.loadListing();
      return;
    }
    this.draw();
  }

  private async loadListing(): Promise<void> {
    if (!this.selectedId) { this.current = null; this.proposal = null; this.draw(); return; }
    this.loading = true; this.proposal = null; this.draw();
    const j = await fetch(`${API}/listing-optimizer/${encodeURIComponent(this.selectedId)}`).then(r => r.ok ? r.json() : null).catch(() => null);
    this.current = j;
    this.description = j ? (j.description || '') : '';
    this.order = j ? (j.images || []).map((im: any) => String(im.id)) : [];
    this.captions = {};
    if (j) for (const im of (j.images || [])) this.captions[String(im.id)] = im.caption || '';
    this.loading = false;
    this.draw();
  }

  private imgById(id: string): any {
    return (this.current?.images || []).find((im: any) => String(im.id) === String(id));
  }

  private draw(): void {
    const body = document.getElementById('lo-body');
    if (!body) return;
    const listingOpts = ['<option value="">— pick a listing —</option>',
      ...this.listings.map((l: any) => `<option value="${this.esc(l.id || l.pms_listing_id)}" ${String(this.selectedId) === String(l.id || l.pms_listing_id) ? 'selected' : ''}>${this.esc(l.name || l.short_name)}</option>`)].join('');
    body.innerHTML = `
      <style>
        .lo-head h2 { margin:0; font-size:19px; font-weight:600; }
        .lo-head .sub { font-size:12px; color:var(--text-muted); }
        .lo-bar { display:flex; gap:10px; align-items:center; margin:14px 0; flex-wrap:wrap; }
        .lo-bar select { padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; min-width:240px; }
        .lo-btn { font-size:13px; font-weight:600; padding:9px 16px; border:none; border-radius:9px; background:var(--accent); color:#fff; cursor:pointer; }
        .lo-btn.ghost { background:#fff; color:var(--text); border:1px solid var(--border); }
        .lo-btn.sync { background:#166534; }
        .lo-btn:disabled { opacity:.55; cursor:default; }
        .lo-cols { display:grid; grid-template-columns:1fr 1fr; gap:16px; align-items:start; }
        @media (max-width:900px) { .lo-cols { grid-template-columns:1fr; } }
        .lo-card { background:var(--card); border:1px solid var(--border); border-radius:12px; padding:14px; }
        .lo-card h4 { margin:0 0 8px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
        .lo-desc { width:100%; min-height:220px; padding:10px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; line-height:1.5; resize:vertical; }
        .lo-cur-desc { font-size:12.5px; line-height:1.55; white-space:pre-wrap; color:var(--text-muted); max-height:240px; overflow:auto; }
        .lo-photos { display:flex; flex-direction:column; gap:8px; }
        .lo-photo { display:flex; gap:10px; align-items:center; border:1px solid var(--border); border-radius:10px; padding:8px; }
        .lo-photo img { width:88px; height:64px; object-fit:cover; border-radius:6px; flex:0 0 auto; }
        .lo-photo-main { flex:1; min-width:0; }
        .lo-cap { width:100%; padding:6px 9px; border:1px solid var(--border); border-radius:7px; font-size:12.5px; }
        .lo-ord { display:flex; flex-direction:column; gap:3px; }
        .lo-ord button { border:1px solid var(--border); background:#fff; border-radius:6px; width:28px; height:26px; cursor:pointer; font-size:11px; }
        .lo-ord button:disabled { opacity:.35; }
        .lo-notes { background:var(--info-soft, #dbeafe); border:1px solid var(--info, #1d4ed8); border-radius:10px; padding:10px 12px; font-size:12.5px; color:var(--info, #1d4ed8); margin:12px 0; }
      </style>
      <div class="lo-head">
        <h2>AI Listing Optimization</h2>
        <span class="sub">pull a listing's live content, let AI improve the copy + photo order + captions, then sync approved changes back to Hostaway</span>
      </div>
      <div class="lo-bar">
        <select id="lo-listing">${listingOpts}</select>
        <button class="lo-btn" id="lo-generate" ${!this.current || this.generating ? 'disabled' : ''}>${this.generating ? 'Generating…' : '✦ Generate suggestions'}</button>
        ${this.proposal ? `<button class="lo-btn sync" id="lo-sync" ${this.syncing ? 'disabled' : ''}>${this.syncing ? 'Syncing…' : '⤴ Approve & sync to Hostaway'}</button>` : ''}
        ${this.note ? `<span style="font-size:12px; color:var(--text-muted);">${this.esc(this.note)}</span>` : ''}
        <button class="lo-btn" id="lo-ctx-toggle" style="margin-left:auto;">${this.ctxOpen ? '▴' : '▾'} AI instructions${this.optimizerContext.trim() ? ' ●' : ''}</button>
      </div>
      ${this.ctxOpen && this.ctxLoadFailed ? `
      <div role="alert" style="border:1px solid var(--border); border-radius:10px; padding:12px 14px; margin:0 0 14px; background:var(--surface, #fff); display:flex; gap:12px; align-items:center; flex-wrap:wrap;">
        <div style="flex:1 1 240px; font-size:13px; color:var(--danger-text);">Couldn't load the saved instructions just now — nothing was changed. Reload them before editing.</div>
        <button class="lo-btn" id="lo-ctx-reload" style="min-height:36px;" ${this.ctxReloading ? 'disabled' : ''}>${this.ctxReloading ? 'Loading…' : 'Reload'}</button>
      </div>` : ''}
      ${this.ctxOpen && !this.ctxLoadFailed ? `
      <div style="border:1px solid var(--border); border-radius:10px; padding:12px 14px; margin:0 0 14px; background:var(--surface, #fff);">
        <div style="font-size:12px; font-weight:600; margin-bottom:6px;">How the optimizer should write — templates, format, brand voice</div>
        <div style="font-size:11.5px; color:var(--text-muted); margin-bottom:8px;">Applied to every listing this tool generates. Paste your listing template/format here (section order, headline style, words to use or avoid, caption tone…).</div>
        <textarea id="lo-ctx" rows="7" maxlength="4000" style="width:100%; border:1px solid var(--border); border-radius:8px; padding:10px; font-size:13px; font-family:inherit; box-sizing:border-box;" placeholder="e.g. Follow our template: hook line → THE SPACE → sleeping arrangements → neighborhood → house rules teaser. Captions sell the experience…">${this.esc(this.optimizerContext)}</textarea>
        <div style="margin-top:8px; display:flex; gap:8px; align-items:center;">
          <button class="lo-btn" id="lo-ctx-save" ${this.ctxSaving ? 'disabled' : ''}>${this.ctxSaving ? 'Saving…' : 'Save instructions'}</button>
          <span style="font-size:11.5px; color:var(--text-faint, #9ca3af);">Saved account-wide · used on the next Generate</span>
        </div>
      </div>` : ''}
      ${this.loading ? `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`
        : (!this.current ? `<div style="font-size:13px; color:var(--text-muted); padding:20px;">Pick a listing to load its current description and photos.</div>` : this.editorHtml())}`;
    this.bind();
  }

  private editorHtml(): string {
    if (this.proposal && this.proposal.notes) {
      // notes shown once generated
    }
    const photos = this.order.map((id, i) => {
      const im = this.imgById(id);
      if (!im) return '';
      return `<div class="lo-photo">
        <div class="lo-ord">
          <button data-lo-up="${i}" ${i === 0 ? 'disabled' : ''}>▲</button>
          <button data-lo-down="${i}" ${i === this.order.length - 1 ? 'disabled' : ''}>▼</button>
        </div>
        <img src="${this.esc(im.url)}" alt="">
        <div class="lo-photo-main">
          <div style="font-size:10.5px; color:var(--text-faint); margin-bottom:3px;">#${i + 1}</div>
          <input class="lo-cap" data-lo-cap="${this.esc(id)}" value="${this.esc(this.captions[id] || '')}" placeholder="Caption">
        </div>
      </div>`;
    }).join('');
    return `
      ${this.proposal && this.proposal.notes ? `<div class="lo-notes">✦ ${this.esc(this.proposal.notes)}</div>` : ''}
      <div class="lo-cols">
        <div class="lo-card">
          <h4>Description ${this.proposal ? '(AI proposed — edit before syncing)' : '(current)'}</h4>
          <textarea class="lo-desc" id="lo-desc">${this.esc(this.description)}</textarea>
        </div>
        <div class="lo-card">
          <h4>Current description (Hostaway)</h4>
          <div class="lo-cur-desc">${this.esc(this.current.description || '(empty)')}</div>
        </div>
      </div>
      <div class="lo-card" style="margin-top:16px;">
        <h4>Photos — order &amp; captions ${this.proposal ? '(AI proposed)' : ''}</h4>
        <div class="lo-photos">${photos || '<div style="font-size:12.5px; color:var(--text-muted);">No photos on this listing.</div>'}</div>
      </div>`;
  }

  private bind(): void {
    const sel = this.container.querySelector<HTMLSelectElement>('#lo-listing');
    sel?.addEventListener('change', () => { this.selectedId = sel.value; void this.loadListing(); });
    const desc = this.container.querySelector<HTMLTextAreaElement>('#lo-desc');
    desc?.addEventListener('input', () => { this.description = desc.value; });
    // R48: instructions card — value mirrors to state on input (survives redraw)
    this.container.querySelector('#lo-ctx-toggle')?.addEventListener('click', () => { this.ctxOpen = !this.ctxOpen; this.draw(); });
    const ctxTa = this.container.querySelector<HTMLTextAreaElement>('#lo-ctx');
    ctxTa?.addEventListener('input', () => { this.optimizerContext = ctxTa.value; });
    // R114 fix2: a failed load shows "couldn't load — Reload" instead of an
    // editable (blank) textarea, so nobody types over instructions they never
    // saw. Reload adopts the stored text AND its version together; the save
    // never re-fetches a fresh version behind the user's back.
    this.container.querySelector('#lo-ctx-reload')?.addEventListener('click', async () => {
      if (this.ctxReloading) return;
      this.ctxReloading = true; this.draw();
      const ctx = await fetch(`${API}/listing-optimizer-context`).then(r => r.ok ? r.json() : null).catch(() => null);
      this.ctxReloading = false;
      if (ctx && ctx.version) {
        this.optimizerContext = ctx.instructions || '';
        this.ctxVersion = ctx.version;
        this.ctxLoadFailed = false;
      }
      this.draw();
    });
    this.container.querySelector('#lo-ctx-save')?.addEventListener('click', async () => {
      if (this.ctxSaving || this.ctxLoadFailed || !this.ctxVersion) return;
      this.ctxSaving = true; this.draw();
      const r = await fetch(`${API}/listing-optimizer-context`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions: this.optimizerContext, base_version: this.ctxVersion }),
      }).catch(() => null);
      const j = r ? await r.json().catch(() => null) : null;
      this.ctxSaving = false;
      if (r && r.ok && j && j.version) this.ctxVersion = j.version;
      // 409 = the stored text changed since this page loaded: nothing saved — reload, then redo
      if (!r || !r.ok) alert(`Could not save the instructions — ${(j && j.error) || 'try again.'}`);
      this.draw();
    });
    this.container.querySelectorAll<HTMLInputElement>('[data-lo-cap]').forEach(el => el.addEventListener('input', () => { this.captions[el.dataset.loCap!] = el.value; }));
    this.container.querySelectorAll<HTMLElement>('[data-lo-up]').forEach(el => el.addEventListener('click', () => { const i = Number(el.dataset.loUp); if (i > 0) { [this.order[i - 1], this.order[i]] = [this.order[i], this.order[i - 1]]; this.draw(); } }));
    this.container.querySelectorAll<HTMLElement>('[data-lo-down]').forEach(el => el.addEventListener('click', () => { const i = Number(el.dataset.loDown); if (i < this.order.length - 1) { [this.order[i + 1], this.order[i]] = [this.order[i], this.order[i + 1]]; this.draw(); } }));

    this.container.querySelector('#lo-generate')?.addEventListener('click', async () => {
      this.generating = true; this.note = ''; this.draw();
      const r = await fetch(`${API}/listing-optimizer/${encodeURIComponent(this.selectedId)}/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const j = await r.json().catch(() => ({} as any));
      this.generating = false;
      if (!j || j.ok === false) { this.note = (j && j.message) || 'Generation failed.'; this.draw(); return; }
      this.proposal = j.proposal;
      this.description = j.proposal.description || this.description;
      // apply proposed order (ids present in current), append any it omitted
      const proposed = (j.proposal.photo_order || []).map(String).filter((id: string) => this.imgById(id));
      const rest = this.order.filter(id => !proposed.includes(id));
      this.order = [...proposed, ...rest];
      for (const [id, cap] of Object.entries(j.proposal.captions || {})) this.captions[String(id)] = String(cap);
      this.note = 'Review the proposal, edit anything, then Approve & sync.';
      this.draw();
    });

    this.container.querySelector('#lo-sync')?.addEventListener('click', async () => {
      if (!confirm('Sync these changes to the LIVE Hostaway listing? This updates the public description, photo order, and captions.')) return;
      this.syncing = true; this.note = ''; this.draw();
      const captions: Record<string, string> = {};
      for (const id of this.order) if ((this.captions[id] || '').trim()) captions[id] = this.captions[id];
      const r = await fetch(`${API}/listing-optimizer/${encodeURIComponent(this.selectedId)}/sync`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: this.description, photo_order: this.order, captions }),
      });
      const j = await r.json().catch(() => ({} as any));
      this.syncing = false;
      if (j && j.ok) { this.note = `Synced to Hostaway — description + ${j.results.images} photo${j.results.images === 1 ? '' : 's'} updated.`; }
      else { this.note = `Sync had errors: ${(j && j.results && j.results.errors || []).slice(0, 2).join('; ') || 'unknown'}`; }
      this.draw();
    });
  }
}
